import Box from "@/ui/atoms/Box";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useForm } from "react-hook-form";
import type { FieldErrors, UseFormRegister } from "react-hook-form";
import type {
    MultipropPropertyInfo,
    PropertyAddressCheck,
    PropertyAddressCheckInfo,
    MultipropBasePropertyInfo,
    MultipropBaseAddressComponent,
    CountryCode,
    PropertyNameCheck,
    NewProperty,
} from "../dealTypes";
import { MUIGlobalStyles as GlobalStyles } from "@proda-ai/ui-kit";
import {
    GoogleMapsPlacePicker,
    GoogleMapsAPIProvider,
    GoogleMapsPlacePickerSkeletonLoader,
} from "@/ui/organisms/GoogleMapsPlacePicker";
import { FieldLabel, Icon, IconBox } from "@proda-ai/ui-kit";
import { formatAddress, creationTestIds } from "./utils";
import FormBadge from "@/ui/atoms/FormBadge";
import type { TextFieldProps } from "@/ui/atoms/TextField";
import TextField from "@/ui/atoms/TextField";

const disabledTextFieldStyles: TextFieldProps["sx"] = {
    "& input:read-only": {
        backgroundColor: "transparent",
    },
    "& input:read-only:hover": {
        backgroundColor: "transparent",
    },
};
import type { TCountryCode } from "@/apis/odin/apiODIN";
import { debounceWithPromise } from "@/utils/debounce";

export type PropertyAddressProps = {
    property?: Partial<NewProperty | MultipropPropertyInfo>;
    databaseId: number;
    isCollapsed?: boolean;
    onSave: (data: Partial<NewProperty | MultipropPropertyInfo>) => void;
    onEdit: () => void;
    setPropertyAddressCheck: (
        data: PropertyAddressCheckInfo,
    ) => Promise<PropertyAddressCheck>;
    setPropertyNameCheck: (
        databaseId: number,
        propertyName: string,
    ) => Promise<PropertyNameCheck>;
    propertyAddressCheck: PropertyAddressCheck | null;
    setIsAddressFormDirty: (isDirty: boolean) => void;
    children?: (props: ChildrenProps) => React.ReactNode;
    configGoogleMapsState: {
        status: "idle" | "pending" | "resolved" | "rejected";
        data: {
            apiKey: string;
        } | null;
    };
};

export const ERROR_MESSAGES = {
    required: "Property name is required",
    alreadyExists: "Property name already exists",
    failedToCheckPropertyName: "There was an error checking the property name",
};

type ChildrenProps = {
    isValid: boolean;
    isDirty: boolean;
    propertyAddressCheck: PropertyAddressCheck | null;
    reset: () => void;
    register: UseFormRegister<MultipropBasePropertyInfo>;
    errors: FieldErrors<MultipropBasePropertyInfo>;
};

const PropertyAddressForm: React.FC<PropertyAddressProps> = ({
    property,
    isCollapsed = false,
    databaseId,
    onSave,
    onEdit,
    setPropertyAddressCheck,
    setPropertyNameCheck,
    propertyAddressCheck,
    setIsAddressFormDirty,
    children = () => null,
    configGoogleMapsState,
}) => {
    const [placeSelected, setPlaceSelected] = useState<boolean>(
        property?.propertyName !== undefined,
    );
    const [shouldValidatePropertyName, setShouldValidatePropertyName] =
        useState<boolean>(false);
    const {
        register,
        handleSubmit,
        setValue,
        trigger,
        reset,
        watch,
        formState: { errors, isValid, isDirty, isValidating },
    } = useForm<MultipropBasePropertyInfo>({
        defaultValues: {
            propertyName: property?.propertyName,
            streetNumber: property?.streetNumber,
            streetName: property?.streetName,
            locality: property?.locality,
            country: property?.country ?? "ZZ",
            zipCode: property?.zipCode,
        },
        mode: "onChange",
    });

    const debouncedSetPropertyAddressCheck = useMemo(
        () => debounceWithPromise(setPropertyAddressCheck, 350),
        [setPropertyAddressCheck],
    );
    const [checkedPropertyAddress, setCheckedPropertyAddress] =
        useState<PropertyAddressCheck | null>(propertyAddressCheck);
    useEffect(() => {
        setCheckedPropertyAddress(propertyAddressCheck);
    }, [propertyAddressCheck]);
    const setPropertyNameCheckRef = useRef(setPropertyNameCheck);
    useEffect(() => {
        setPropertyNameCheckRef.current = setPropertyNameCheck;
    }, [setPropertyNameCheck]);

    // to prevent infinite render -> address check -> re-render loop
    const lastAddressRef = useRef<Partial<MultipropPropertyInfo> | null>(null);

    const onTrimSubmit = useCallback(
        (data: MultipropBasePropertyInfo) => {
            void onSave({
                ...property,
                ...data,
                propertyName: data.propertyName.trim(),
                streetName: data.streetName?.trim(),
                locality: data.locality?.trim(),
                country: data.country,
                zipCode: data.zipCode?.trim(),
            } as Parameters<typeof onSave>[0]);
        },
        [onSave, property],
    );

    const watchedAddress = watch();
    useEffect(() => {
        const checkAddress = () => {
            const {
                streetName,
                streetNumber,
                zipCode,
                propertyName,
                locality,
                country,
            } = watchedAddress;
            const currentAddress = {
                streetName,
                streetNumber,
                zipCode,
                propertyName,
                locality,
                country,
            };
            if (
                JSON.stringify(currentAddress) ===
                JSON.stringify(lastAddressRef.current)
            ) {
                // the functions below cause render loops so we
                // don't call them if the address hasn't changed
                return;
            }
            lastAddressRef.current = currentAddress;
            void debouncedSetPropertyAddressCheck({
                ...watchedAddress,
                databaseId,
            })
                .then(setCheckedPropertyAddress)
                .catch(() => {
                    // Backend 503s when Excelsior is unreachable; without a
                    // result the duplicate-address warning simply can't show.
                    setCheckedPropertyAddress(null);
                });
            void setIsAddressFormDirty(isDirty);
        };
        void checkAddress();
    }, [
        watchedAddress,
        debouncedSetPropertyAddressCheck,
        setIsAddressFormDirty,
        databaseId,
        trigger,
        isDirty,
    ]);

    useEffect(
        () => debouncedSetPropertyAddressCheck.cancel,
        [debouncedSetPropertyAddressCheck],
    );

    const initialDataPropertyName = property?.propertyName?.trim() ?? "";
    const validatePropertyName = useCallback(
        async (propertyName: string) => {
            const propertyNameTrimmed = propertyName.trim();
            if (!propertyNameTrimmed) {
                return ERROR_MESSAGES.required;
            }
            try {
                const propertyNameCheckState =
                    await setPropertyNameCheckRef.current(
                        databaseId,
                        propertyNameTrimmed,
                    );
                if (propertyNameCheckState.success === false) {
                    return ERROR_MESSAGES.failedToCheckPropertyName;
                }
                if (
                    propertyNameCheckState.isExactMatch &&
                    propertyNameTrimmed !== initialDataPropertyName
                ) {
                    return ERROR_MESSAGES.alreadyExists;
                }

                return true;
            } catch {
                // Backend 503s when Excelsior is unreachable — surface it as a
                // validation message instead of hanging the form's validation.
                return ERROR_MESSAGES.failedToCheckPropertyName;
            }
        },
        [databaseId, initialDataPropertyName],
    );

    const validatePropertyNameDebounced = useMemo(
        // A cancelled run resolves to "valid"; RHF can't handle rejections.
        () =>
            debounceWithPromise(
                validatePropertyName,
                350,
                {},
                { resolveWith: true },
            ),
        [validatePropertyName],
    );

    useEffect(
        () => validatePropertyNameDebounced.cancel,
        [validatePropertyNameDebounced],
    );

    useEffect(() => {
        if (placeSelected && shouldValidatePropertyName) {
            void trigger("propertyName");
            setShouldValidatePropertyName(false);
        }
    }, [placeSelected, shouldValidatePropertyName, trigger]);

    const handlePlaceChange = (
        place: google.maps.places.PlaceResult | null,
    ) => {
        if (!place) return;
        const addrRecord: Record<string, MultipropBaseAddressComponent> =
            Object.fromEntries(
                (place?.address_components ?? []).toReversed().flatMap(ac =>
                    ac.types.map((t: string) => [
                        t,
                        {
                            shortName: ac.short_name ?? "",
                            longName: ac.long_name ?? "",
                        },
                    ]),
                ),
            );
        let streetNumber = null;
        let streetName = null;
        let city = null;
        let postalCode = null;
        let country: CountryCode = "ZZ";

        place?.address_components?.forEach(component => {
            if (!component.long_name) {
                return;
            }
            const types = component.types;

            if (types.includes("street_number")) {
                streetNumber = component.long_name;
            }
            if (types.includes("route")) {
                streetName = component.long_name;
            }
            if (
                types.includes("postal_town") ||
                types.includes("locality") ||
                types.includes("administrative_area_level_3")
            ) {
                city = component.long_name;
            }
            if (types.includes("postal_code")) {
                postalCode = component.long_name;
            }
            if (types.includes("country")) {
                country = component.short_name as TCountryCode;
            }
            if (types.includes("administrative_area_level_1")) {
                setValue("regionLevel1", component.long_name);
                setValue("regionLevel1Short", component.short_name ?? "");
            }
            if (types.includes("administrative_area_level_2")) {
                setValue("regionLevel2", component.long_name);
            }
        });

        setValue("streetNumber", streetNumber, {
            shouldDirty: true,
        });
        void trigger("streetNumber");
        setValue("streetName", streetName, { shouldDirty: true });
        void trigger("streetName");
        setValue("locality", city, { shouldDirty: true });
        void trigger("locality");
        setValue("country", country as CountryCode, {
            shouldDirty: true,
        });
        void trigger("country");
        setValue("zipCode", postalCode, { shouldDirty: true });
        void trigger("zipCode");
        setValue("fullAddress", place?.formatted_address ?? "");
        setValue("latitude", place?.geometry?.location?.lat());
        setValue("longitude", place?.geometry?.location?.lng());
        setValue("component", addrRecord);

        if (place?.name !== undefined) {
            setValue("propertyName", place.name, { shouldDirty: true });
            setValue("googleName", place.name);
            setShouldValidatePropertyName(true);
        }
        setPlaceSelected(true);
    };

    const googleSearchStyles = {
        ".pac-container:after": {
            "background-image": "none !important",
            height: "0px",
            padding: "0",
            margin: "0",
        },
    };

    const isFormPopulated = isDirty || property?.propertyName !== undefined;
    const isFormValid =
        Object.values(errors).length === 0 && !isValidating && isFormPopulated;
    const formBadgeStatus = isFormValid && isCollapsed ? "valid" : "empty";

    const cardHeader = (
        <Box
            sx={{
                display: "flex",
                paddingBottom: "1rem",
                marginBottom: "1rem",
                borderBottom: "1px solid var(--proda-white-150)",
                justifyContent: "space-between",
            }}
        >
            <Box
                sx={{
                    fontSize: "0.8rem",
                    display: "flex",
                }}
            >
                <FormBadge
                    status={formBadgeStatus}
                    sx={{ marginRight: "0.4rem" }}
                />
                <Box
                    sx={{
                        fontWeight: "bold",
                        marginRight: "0.3rem",
                    }}
                    data-testid={creationTestIds.dealInfoFormTitle}
                >
                    Address
                </Box>
                (required)
            </Box>
            {isCollapsed && (
                <IconBox>
                    <Icon
                        icon="pen-line"
                        onClick={onEdit}
                        data-testid={
                            creationTestIds.propertyFormEditAddressButton
                        }
                    />
                </IconBox>
            )}
        </Box>
    );

    if (isCollapsed) {
        const fullAddress = formatAddress(watchedAddress);
        return (
            <Box
                sx={{
                    maxWidth: "600px",
                    margin: "2rem auto",
                    backgroundColor: "var(--proda-white-110)",
                    padding: "1rem",
                }}
            >
                {cardHeader}
                <Box>
                    <Box
                        sx={{
                            fontSize: "0.8rem",
                            display: "flex",
                        }}
                    >
                        <Box
                            sx={{
                                fontWeight: "bold",
                                marginRight: "0.3rem",
                            }}
                        >
                            {watchedAddress.propertyName}
                            {fullAddress ? "," : ""}
                        </Box>
                        {fullAddress}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <GlobalStyles styles={googleSearchStyles} />
            <Box
                sx={{
                    maxWidth: "600px",
                    margin: "2rem auto",
                    backgroundColor: "var(--proda-white-110)",
                    padding: "1rem",
                }}
            >
                {cardHeader}
                <Box sx={{ marginBottom: "1.5rem" }}>
                    <FieldLabel
                        label="Search for property"
                        fieldId="property-search"
                        mb={1}
                    />
                    <Box
                        sx={{
                            width: "100%",
                        }}
                    >
                        {configGoogleMapsState.status === "resolved" &&
                        configGoogleMapsState.data !== null ? (
                            <GoogleMapsAPIProvider
                                apiKey={configGoogleMapsState.data.apiKey}
                            >
                                <GoogleMapsPlacePicker
                                    onPlaceSelect={handlePlaceChange}
                                />
                            </GoogleMapsAPIProvider>
                        ) : (
                            <GoogleMapsPlacePickerSkeletonLoader />
                        )}
                    </Box>
                </Box>
                <form
                    onSubmit={(...args) =>
                        void handleSubmit(onTrimSubmit)(...args)
                    }
                >
                    <Box sx={{ marginBottom: "1rem" }}>
                        <TextField
                            {...register("propertyName", {
                                required: "Property Name is required",
                                validate: validatePropertyNameDebounced,
                            })}
                            id="propertyName"
                            sx={disabledTextFieldStyles}
                            label="Name*"
                            hasErrors={!!errors.propertyName}
                            errorMessage={errors.propertyName?.message}
                            placeholder="Name"
                            data-1p-ignore
                            autoComplete="off"
                            disabled={!placeSelected}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                        }}
                    >
                        <Box sx={{ marginBottom: "1rem", width: "30%" }}>
                            <TextField
                                {...register("streetNumber", {
                                    maxLength: {
                                        value: 64,
                                        message: "Street no. is too long",
                                    },
                                })}
                                id="streetNumber"
                                label="Street No."
                                sx={disabledTextFieldStyles}
                                placeholder="Street No."
                                hasErrors={!!errors.streetNumber}
                                errorMessage={errors.streetNumber?.message}
                                disabled={true}
                                data-1p-ignore
                                autoComplete="off"
                            />
                        </Box>
                        <Box sx={{ marginBottom: "1rem", width: "70%" }}>
                            <TextField
                                {...register("streetName")}
                                id="streetName"
                                label="Street Name"
                                sx={disabledTextFieldStyles}
                                placeholder="Street Name"
                                hasErrors={!!errors.streetName}
                                errorMessage={errors.streetName?.message}
                                disabled={true}
                                data-1p-ignore
                                autoComplete="off"
                            />
                        </Box>
                    </Box>
                    <Box sx={{ marginBottom: "1rem" }}>
                        <TextField
                            {...register("locality")}
                            id="city"
                            label="City"
                            sx={disabledTextFieldStyles}
                            placeholder="City"
                            hasErrors={!!errors.locality}
                            errorMessage={errors.locality?.message}
                            disabled={true}
                            data-1p-ignore
                            autoComplete="off"
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                            gap: "1rem",
                        }}
                    >
                        <Box
                            sx={{
                                marginBottom: "1rem",
                                width: "50%",
                            }}
                        >
                            <TextField
                                {...register("country", {
                                    required: "Country is required",
                                })}
                                id="country"
                                label="Country*"
                                placeholder="Country"
                                sx={disabledTextFieldStyles}
                                hasErrors={!!errors.country}
                                errorMessage={errors.country?.message}
                                disabled={true}
                                data-1p-ignore
                                autoComplete="off"
                            />
                        </Box>
                        <Box sx={{ marginBottom: "1rem", width: "50%" }}>
                            <TextField
                                {...register("zipCode")}
                                id="zipCode"
                                label="ZIP Code"
                                placeholder="ZIP Code"
                                sx={disabledTextFieldStyles}
                                hasErrors={!!errors.zipCode}
                                errorMessage={errors.zipCode?.message}
                                disabled={true}
                                data-1p-ignore
                                autoComplete="off"
                            />
                        </Box>
                    </Box>
                    {children({
                        isValid,
                        isDirty,
                        propertyAddressCheck: checkedPropertyAddress,
                        reset: () =>
                            reset({
                                propertyName: property?.propertyName ?? "",
                                streetNumber: property?.streetNumber ?? "",
                                streetName: property?.streetName ?? "",
                                locality: property?.locality ?? "",
                                country: property?.country ?? "ZZ",
                                zipCode: property?.zipCode ?? "",
                            }),
                        register,
                        errors,
                    })}

                    <Box
                        component="button"
                        type="submit"
                        sx={{
                            padding: "0.5rem 2rem",
                            border: "none",
                            borderRadius: "4px",
                            marginTop: "1rem",
                            backgroundColor: !isFormValid
                                ? "var(--disabled-bg)"
                                : "var(--primary-color)",
                            color: "var(--proda-white)",
                            cursor: !isFormValid ? "not-allowed" : "pointer",
                        }}
                        data-testid={
                            creationTestIds.propertyFormConfirmAddressButton
                        }
                        disabled={!isFormValid}
                    >
                        Confirm Address
                    </Box>
                </form>
            </Box>
        </>
    );
};

export default PropertyAddressForm;
